/**
 * BOQ Spreadsheet
 * Excel-like inline-editable spreadsheet for BOQ / Materials Schedule tracking.
 * Renders categories as header rows with items, subtotals, labor, and category totals.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, TextField, Select, MenuItem, IconButton, Tooltip,
  Button, Chip, Collapse
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import PaymentsIcon from '@mui/icons-material/Payments';
import { brand, status as statusColors } from '../../theme/colors';
import BOQLaborPaymentDialog from './BOQLaborPaymentDialog';
import BOQLaborPaymentHistory from './BOQLaborPaymentHistory';

const UNIT_OPTIONS = ['bags', 'm³', 'pcs', 'kg', 'bdl', 'packs', 'rolls', 'sheets', 'meters', 'liters', 'tons', 'loads'];
const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Partial', 'Complete', 'Ordered'];

const TEMPLATE_CATEGORIES = [
  { name: 'CATEGORY 1', items: [] },
  { name: 'CATEGORY 2', items: [] },
];

const fmt = (n) => {
  const val = parseFloat(n || 0);
  return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (d) => {
  if (!d) return '';
  // Parse YYYY-MM-DD as local date (not UTC) by splitting
  const parts = String(d).split('T')[0].split('-');
  if (parts.length !== 3) return d;
  const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
};

// Inline editable cell
function EditableCell({ value, onChange, type = 'text', options, align = 'left', disabled, sx = {} }) {
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => { setLocalVal(value ?? ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  if (disabled) {
    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', textAlign: align, py: 0.5, px: 0.5, ...sx }}>
        {type === 'number' ? fmt(value) : (value || '')}
      </Typography>
    );
  }

  if (type === 'select') {
    return (
      <Select
        size="small"
        value={localVal || options?.[0] || ''}
        onChange={(e) => { setLocalVal(e.target.value); onChange(e.target.value); }}
        variant="standard"
        disableUnderline
        sx={{ fontSize: '0.8rem', width: '100%', '& .MuiSelect-select': { py: 0.5 }, ...sx }}
      >
        {(options || []).map(o => <MenuItem key={o} value={o} sx={{ fontSize: '0.8rem' }}>{o}</MenuItem>)}
      </Select>
    );
  }

  if (!editing) {
    return (
      <Box
        onClick={() => setEditing(true)}
        sx={{
          cursor: 'pointer', minHeight: 28, display: 'flex', alignItems: 'center',
          px: 0.5, py: 0.25, borderRadius: 0.5,
          '&:hover': { bgcolor: '#f0fdf4' },
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start'
        }}
      >
        <Typography variant="body2" sx={{ fontFamily: type === 'number' ? 'monospace' : 'inherit', fontSize: '0.82rem', ...sx }}>
          {type === 'number' && value ? fmt(value) : (value || '\u00A0')}
        </Typography>
      </Box>
    );
  }

  return (
    <TextField
      inputRef={inputRef}
      size="small"
      variant="standard"
      type={type === 'number' ? 'number' : 'text'}
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={() => {
        setEditing(false);
        const newVal = type === 'number' ? (parseFloat(localVal) || 0) : localVal;
        if (String(newVal) !== String(value)) onChange(newVal);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
          setEditing(false);
          const newVal = type === 'number' ? (parseFloat(localVal) || 0) : localVal;
          if (String(newVal) !== String(value)) onChange(newVal);
        }
        if (e.key === 'Escape') { setLocalVal(value ?? ''); setEditing(false); }
      }}
      inputProps={{ style: { fontSize: '0.82rem', fontFamily: type === 'number' ? 'monospace' : 'inherit', textAlign: align, padding: '4px 2px' } }}
      sx={{ width: '100%' }}
      InputProps={{ disableUnderline: false }}
    />
  );
}

// Date cell: shows formatted date, opens native date picker on click
function DateCell({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  // Normalize to YYYY-MM-DD for the input (avoid UTC shift)
  const isoVal = value ? String(value).split('T')[0] : '';

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.showPicker?.();
    }
  }, [editing]);

  if (!editing) {
    return (
      <Box
        onClick={() => setEditing(true)}
        sx={{ cursor: 'pointer', minHeight: 28, display: 'flex', alignItems: 'center', px: 0.5, py: 0.25, borderRadius: 0.5, '&:hover': { bgcolor: '#f0fdf4' } }}
      >
        <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
          {fmtDate(value) || '\u00A0'}
        </Typography>
      </Box>
    );
  }

  return (
    <TextField
      inputRef={inputRef}
      size="small"
      variant="standard"
      type="date"
      value={isoVal}
      onChange={(e) => {
        onChange(e.target.value);
        setEditing(false);
      }}
      onBlur={() => setEditing(false)}
      inputProps={{ style: { fontSize: '0.82rem', padding: '4px 2px' } }}
      sx={{ width: '100%' }}
      InputProps={{ disableUnderline: false }}
    />
  );
}

// ─── Main Component ───

export default function BOQSpreadsheet({
  categories: initialCategories,
  isCreateMode = false,
  readOnly = false,
  title = '',
  metadata = {},
  onSave,
  onUpdateItem,
  onDeleteItem,
  onAddItem,
  onAddCategory,
  onUpdateCategory,
  onDeleteCategory,
  onLogLaborPayment,
  onLoadLaborPayments,
  saving = false,
  seedCategories = null
}) {
  // For create mode, manage state locally
  const [localCategories, setLocalCategories] = useState(
    isCreateMode
      ? TEMPLATE_CATEGORIES.map(c => ({ ...c, labor_cost: 0, items: [...c.items] }))
      : []
  );

  // Replace the local working copy when the caller supplies a seed (e.g. an
  // AI-drafted schedule) — create mode only; the contractor edits from there.
  React.useEffect(() => {
    if (isCreateMode && Array.isArray(seedCategories) && seedCategories.length > 0) {
      setLocalCategories(seedCategories.map(c => ({
        name: c.name,
        labor_cost: c.labor_cost || 0,
        items: (c.items || []).map(i => ({ ...i }))
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedCategories]);

  const categories = isCreateMode ? localCategories : (initialCategories || []);
  const [laborPaymentCategory, setLaborPaymentCategory] = useState(null);
  const [laborPaymentLoading, setLaborPaymentLoading] = useState(false);
  const [expandedLaborHistory, setExpandedLaborHistory] = useState(null);
  const [laborPaymentData, setLaborPaymentData] = useState({});

  const handleSubmitLaborPayment = async (data) => {
    if (!laborPaymentCategory || !onLogLaborPayment) return;
    setLaborPaymentLoading(true);
    try {
      await onLogLaborPayment(laborPaymentCategory.id, data);
      setLaborPaymentCategory(null);
      setLaborPaymentData(prev => {
        const next = { ...prev };
        delete next[laborPaymentCategory.id];
        return next;
      });
    } finally {
      setLaborPaymentLoading(false);
    }
  };

  const toggleLaborHistory = async (categoryId) => {
    if (expandedLaborHistory === categoryId) {
      setExpandedLaborHistory(null);
      return;
    }
    if (!laborPaymentData[categoryId] && onLoadLaborPayments) {
      const payments = await onLoadLaborPayments(categoryId);
      setLaborPaymentData(prev => ({ ...prev, [categoryId]: payments }));
    }
    setExpandedLaborHistory(categoryId);
  };

  // ─── Create Mode: Local State Mutations ───

  const updateLocalItem = (catIdx, itemIdx, field, value) => {
    setLocalCategories(prev => prev.map((c, ci) =>
      ci === catIdx ? {
        ...c,
        items: c.items.map((item, ii) => {
          if (ii !== itemIdx) return item;
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unit_price') {
            updated.amount = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.unit_price) || 0);
          }
          return updated;
        })
      } : c
    ));
  };

  const addLocalItem = (catIdx, itemType) => {
    const newItem = itemType === 'labour'
      ? { item_name: '', quantity: 1, unit: 'lump sum', unit_price: '', amount: 0, quantity_purchased: 0, status: 'Not Started', notes: '', item_type: 'labour' }
      : { item_name: '', quantity: '', unit: 'bags', unit_price: '', amount: 0, quantity_purchased: 0, status: 'Not Started', notes: '', item_type: 'material' };
    setLocalCategories(prev => prev.map((c, ci) =>
      ci === catIdx ? { ...c, items: [...c.items, newItem] } : c
    ));
  };

  const deleteLocalItem = (catIdx, itemIdx) => {
    setLocalCategories(prev => prev.map((c, ci) =>
      ci === catIdx ? { ...c, items: c.items.filter((_, ii) => ii !== itemIdx) } : c
    ));
  };

  const updateLocalCategory = (catIdx, field, value) => {
    setLocalCategories(prev => prev.map((c, ci) =>
      ci === catIdx ? { ...c, [field]: value } : c
    ));
  };

  const addLocalCategory = () => {
    setLocalCategories(prev => [...prev, { name: 'NEW CATEGORY', labor_cost: 0, items: [] }]);
  };

  const deleteLocalCategory = (catIdx) => {
    setLocalCategories(prev => prev.filter((_, ci) => ci !== catIdx));
  };

  // ─── Edit Mode: API Mutations ───

  const handleCellChange = useCallback((item, field, value) => {
    if (isCreateMode || readOnly) return; // handled by local state, or blocked in read-only mode
    if (onUpdateItem) {
      const updates = { [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? value : item.quantity;
        const price = field === 'unit_price' ? value : item.unit_price;
        updates.amount = (parseFloat(qty) || 0) * (parseFloat(price) || 0);
      }
      onUpdateItem(item.id, updates);
    }
  }, [isCreateMode, onUpdateItem, readOnly]);

  // ─── Cell Styles ───
  const cellSx = { py: 0.5, px: 1, borderRight: '1px solid #e5e7eb', fontSize: '0.82rem', verticalAlign: 'middle' };
  const headerCellSx = { ...cellSx, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#fff', whiteSpace: 'nowrap' };

  return (
    <Box>
      {/* Title Header */}
      {title && (
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            {title}
          </Typography>
          {metadata.contractor && (
            <Typography variant="caption" color="text.secondary">
              Contractor: {metadata.contractor} | Quote: {metadata.quoteRef || '—'} | Date: {metadata.date || '—'} | Stage: {metadata.stage || '—'}
            </Typography>
          )}
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#d1d5db', borderRadius: 1, overflow: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1200, borderCollapse: 'collapse', '& td, & th': { borderBottom: '1px solid #e5e7eb' } }}>
          {/* Column Headers */}
          <TableHead>
            <TableRow sx={{ background: brand.primaryDeep }}>
              <TableCell sx={{ ...headerCellSx, width: 40 }}>#</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 200 }}>Item</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 80 }} align="right">Qty Reqd</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 70 }}>Unit</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 90 }} align="right">Unit Price ($)</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 110 }} align="right">Budgeted Amt ($)</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 80, bgcolor: '#065f46' }} align="right">Qty Bought</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 90 }} align="right">Qty Remaining</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 100 }} align="right">Amt Spent ($)</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 110 }} align="right">Transfer Charges ($)</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 110 }} align="right">Amt Remaining ($)</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 110 }}>Status</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 110 }}>Date Purchased</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 160 }}>Notes / Comments</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 40 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {categories.map((cat, catIdx) => {
              const items = cat.items || [];
              const materialsSubtotal = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
              const laborCost = parseFloat(cat.labor_cost) || 0;
              const categoryTotal = materialsSubtotal + laborCost;
              const totalSpent = items.reduce((s, i) => s + (parseFloat(i.purchase_summary?.total_spent) || 0), 0);
              const totalTransferCharges = items.reduce((s, i) => s + (parseFloat(i.transfer_charges) || 0), 0);
              const laborPaidToDate = parseFloat(cat.labor_paid_to_date || 0);
              const laborBalance = laborCost - laborPaidToDate;
              const laborStatus = cat.labor_status || (laborPaidToDate >= laborCost && laborCost > 0 ? 'Paid' : laborPaidToDate > 0 ? 'Partial' : 'Not Started');

              return (
                <React.Fragment key={cat.id || catIdx}>
                  {/* Category Header Row */}
                  {!readOnly && (
                  <TableRow>
                    <TableCell
                      colSpan={14}
                      sx={{
                        bgcolor: '#c2410c', color: '#fff', fontWeight: 800,
                        fontSize: '0.85rem', textTransform: 'uppercase',
                        letterSpacing: '0.05em', py: 1, px: 1.5,
                        borderRight: 'none'
                      }}
                    >
                      {isCreateMode ? (
                        <EditableCell
                          value={cat.name}
                          onChange={(v) => updateLocalCategory(catIdx, 'name', v.toUpperCase())}
                          sx={{ color: '#fff', fontWeight: 800, fontSize: '0.85rem' }}
                        />
                      ) : cat.name}
                    </TableCell>
                    <TableCell sx={{ bgcolor: '#c2410c', borderRight: 'none', py: 1 }}>
                      {isCreateMode && (
                        <IconButton size="small" onClick={() => deleteLocalCategory(catIdx)} sx={{ color: '#fff', opacity: 0.7 }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                  )}

                  {/* Item Rows */}
                  {items.map((item, itemIdx) => {
                    const isLabour = item.item_type === 'labour';
                    const qty = parseFloat(item.quantity) || 0;
                    const price = parseFloat(item.unit_price) || 0;
                    const budgeted = isLabour ? price : qty * price;
                    const qtyBought = parseFloat(item.quantity_purchased) || 0;
                    const qtyRemaining = isLabour ? 0 : qty - qtyBought;
                    const amtSpent = parseFloat(item.purchase_summary?.total_spent) || 0;
                    const amtRemaining = isLabour ? (budgeted - amtSpent) : (budgeted - (qtyBought * price));
                    const itemStatus = isLabour
                      ? (amtSpent >= budgeted && budgeted > 0 ? 'Complete' : amtSpent > 0 ? 'Partial' : 'Not Started')
                      : (qtyBought >= qty && qty > 0 ? 'Complete' : qtyBought > 0 ? 'Partially Bought' : 'Not Started');

                    const onItemChange = isCreateMode
                      ? (field, val) => updateLocalItem(catIdx, itemIdx, field, val)
                      : (field, val) => handleCellChange(item, field, val);

                    return (
                      <TableRow key={item.id || itemIdx} sx={{ '&:hover': { bgcolor: '#fafafa' }, bgcolor: isLabour ? '#f5f3ff' : 'inherit' }}>
                        <TableCell sx={{ ...cellSx, color: '#9ca3af', fontWeight: 600 }}>{itemIdx + 1}</TableCell>
                        <TableCell sx={cellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isLabour && <Chip label="L" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#7c3aed', color: '#fff', fontWeight: 700 }} />}
                            <EditableCell value={item.item_name} onChange={(v) => onItemChange('item_name', v)} disabled={readOnly} />
                          </Box>
                        </TableCell>
                        {isLabour ? (
                          <>
                            <TableCell colSpan={3} sx={{ ...cellSx, color: '#9ca3af', fontStyle: 'italic', fontSize: '0.75rem', textAlign: 'center' }}>
                              Lump Sum
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell sx={cellSx} align="right">
                              <EditableCell value={item.quantity} onChange={(v) => onItemChange('quantity', v)} type="number" align="right" disabled={readOnly} />
                            </TableCell>
                            <TableCell sx={cellSx}>
                              <EditableCell value={item.unit} onChange={(v) => onItemChange('unit', v)} type="select" options={UNIT_OPTIONS} disabled={readOnly} />
                            </TableCell>
                            <TableCell sx={cellSx} align="right">
                              <EditableCell value={item.unit_price} onChange={(v) => onItemChange('unit_price', v)} type="number" align="right" disabled={readOnly} />
                            </TableCell>
                          </>
                        )}
                        <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontWeight: 600, bgcolor: '#fffbeb' }} align="right">
                          {isLabour ? (
                            <EditableCell value={item.unit_price || 0} onChange={(v) => onItemChange('unit_price', v)} type="number" align="right" disabled={readOnly} />
                          ) : (
                            <EditableCell value={budgeted} disabled align="right" type="number" />
                          )}
                        </TableCell>
                        {isLabour ? (
                          <TableCell colSpan={2} sx={{ ...cellSx, color: '#9ca3af', textAlign: 'center' }}>—</TableCell>
                        ) : (
                          <>
                            <TableCell sx={{ ...cellSx, bgcolor: '#ecfdf5' }} align="right">
                              <EditableCell
                                value={qtyBought}
                                onChange={(v) => onItemChange('quantity_purchased', v)}
                                type="number"
                                align="right"
                                disabled={readOnly || (!isCreateMode && !onUpdateItem)}
                              />
                            </TableCell>
                            <TableCell sx={{ ...cellSx, fontFamily: 'monospace', color: qtyRemaining <= 0 && qty > 0 ? statusColors.success : 'inherit' }} align="right">
                              <EditableCell value={qtyRemaining} disabled align="right" type="number" />
                            </TableCell>
                          </>
                        )}
                        <TableCell sx={{ ...cellSx, fontFamily: 'monospace' }} align="right">
                          <EditableCell value={amtSpent} disabled align="right" type="number" />
                        </TableCell>
                        <TableCell sx={{ ...cellSx, bgcolor: '#fef3c7' }} align="right">
                          <EditableCell
                            value={item.transfer_charges || 0}
                            onChange={(v) => onItemChange('transfer_charges', v)}
                            type="number"
                            align="right"
                            disabled={readOnly}
                          />
                        </TableCell>
                        <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontWeight: 600 }} align="right">
                          <EditableCell value={amtRemaining} disabled align="right" type="number" />
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <EditableCell value={item.status || itemStatus} onChange={(v) => onItemChange('status', v)} type="select" options={STATUS_OPTIONS} disabled={readOnly} />
                        </TableCell>
                        <TableCell sx={cellSx}>
                          {isCreateMode ? (
                            <EditableCell value={item.purchase_date || ''} onChange={(v) => onItemChange('purchase_date', v)} />
                          ) : readOnly ? (
                            <EditableCell value={fmtDate(item.purchase_date)} disabled />
                          ) : (
                            <DateCell value={item.purchase_date} onChange={(v) => onItemChange('purchase_date', v)} />
                          )}
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <EditableCell value={item.notes || ''} onChange={(v) => onItemChange('notes', v)} disabled={readOnly} />
                        </TableCell>
                        <TableCell sx={{ ...cellSx, borderRight: 'none' }}>
                          {isCreateMode ? (
                            <IconButton size="small" onClick={() => deleteLocalItem(catIdx, itemIdx)} sx={{ color: '#d1d5db', '&:hover': { color: statusColors.error } }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          ) : !readOnly && onDeleteItem && (
                            <IconButton size="small" onClick={() => onDeleteItem(item.id)} sx={{ color: '#d1d5db', '&:hover': { color: statusColors.error } }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Add Item Row */}
                  <TableRow>
                    <TableCell colSpan={15} sx={{ py: 0.5, px: 1.5, borderRight: 'none' }}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => isCreateMode ? addLocalItem(catIdx, 'material') : (onAddItem && onAddItem(cat.id, 'material'))}
                        sx={{ color: brand.primary, fontSize: '0.75rem', textTransform: 'none', mr: 2 }}
                      >
                        Add Material
                      </Button>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => isCreateMode ? addLocalItem(catIdx, 'labour') : (onAddItem && onAddItem(cat.id, 'labour'))}
                        sx={{ color: '#7c3aed', fontSize: '0.75rem', textTransform: 'none' }}
                      >
                        Add Labour
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Materials Subtotal Row */}
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell sx={cellSx} />
                    <TableCell colSpan={4} sx={{ ...cellSx, fontWeight: 700, fontSize: '0.8rem' }}>Materials Subtotal</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700, fontFamily: 'monospace', bgcolor: '#fef9c3' }} align="right">
                      {fmt(materialsSubtotal)}
                    </TableCell>
                    <TableCell colSpan={2} sx={cellSx} />
                    <TableCell sx={{ ...cellSx, fontWeight: 600, fontFamily: 'monospace' }} align="right">{fmt(totalSpent)}</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600, fontFamily: 'monospace' }} align="right">{fmt(totalTransferCharges)}</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700, fontFamily: 'monospace' }} align="right">{fmt(materialsSubtotal - totalSpent)}</TableCell>
                    <TableCell colSpan={4} sx={{ ...cellSx, borderRight: 'none' }} />
                  </TableRow>

                  {/* Labour Row */}
                  <TableRow>
                    <TableCell sx={cellSx} />
                    <TableCell colSpan={4} sx={{ ...cellSx, fontWeight: 700, fontSize: '0.8rem' }}>Labour</TableCell>
                    <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontWeight: 600 }} align="right">
                      {isCreateMode ? (
                        <EditableCell value={cat.labor_cost} onChange={(v) => updateLocalCategory(catIdx, 'labor_cost', v)} type="number" align="right" />
                      ) : (
                        <EditableCell
                          value={cat.labor_cost}
                          onChange={(v) => onUpdateCategory && onUpdateCategory(cat.id, { labor_cost: v })}
                          type="number" align="right"
                          disabled={readOnly}
                        />
                      )}
                    </TableCell>
                    <TableCell colSpan={2} sx={cellSx} />
                    <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontWeight: 600 }} align="right">
                      {fmt(laborPaidToDate)}
                    </TableCell>
                    <TableCell sx={cellSx} />
                    <TableCell sx={{ ...cellSx, fontWeight: 600, fontFamily: 'monospace' }} align="right">
                      {fmt(laborBalance)}
                      {laborStatus === 'Paid' && (
                        <Chip icon={<CheckCircleIcon sx={{ fontSize: 12 }} />} label="Paid" size="small"
                          sx={{ ml: 0.5, height: 18, fontSize: '0.6rem', bgcolor: statusColors.successBg, color: statusColors.success }} />
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <EditableCell value={laborStatus === 'Paid' ? 'Complete' : laborStatus} disabled type="select" options={[...STATUS_OPTIONS, 'Paid', 'Overpaid']} />
                    </TableCell>
                    <TableCell sx={cellSx}>{cat.labor_last_payment_date ? fmtDate(cat.labor_last_payment_date) : ''}</TableCell>
                    <TableCell sx={cellSx}>
                      {cat.labor_payment_count > 0 ? `${cat.labor_payment_count} payment${cat.labor_payment_count === 1 ? '' : 's'}` : ''}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, borderRight: 'none' }}>
                      {!isCreateMode && (
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title="Log Labour Payment">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setLaborPaymentCategory(cat)}
                                disabled={!onLogLaborPayment}
                                sx={{ color: brand.primary }}
                              >
                                <PaymentsIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Labour Payment History">
                            <IconButton
                              size="small"
                              onClick={() => toggleLaborHistory(cat.id)}
                              sx={{ color: expandedLaborHistory === cat.id ? brand.primary : 'text.secondary' }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {!isCreateMode && (
                    <TableRow>
                      <TableCell colSpan={15} sx={{ py: 0, borderBottom: expandedLaborHistory === cat.id ? undefined : 'none' }}>
                        <Collapse in={expandedLaborHistory === cat.id}>
                          <Box sx={{ py: 2, px: 2, bgcolor: '#fafafa', borderRadius: 1, my: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Labour Payment History</Typography>
                            <BOQLaborPaymentHistory payments={laborPaymentData[cat.id] || []} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Category Total Row */}
                  <TableRow sx={{ bgcolor: brand.primary + '12' }}>
                    <TableCell sx={cellSx} />
                    <TableCell colSpan={4} sx={{ ...cellSx, fontWeight: 800, fontSize: '0.85rem', color: brand.primaryDark, textTransform: 'uppercase' }}>
                      Total {cat.name}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 800, fontFamily: 'monospace', fontSize: '0.9rem', color: brand.primaryDark }} align="right">
                      {fmt(categoryTotal)}
                    </TableCell>
                    <TableCell colSpan={2} sx={cellSx} />
                    <TableCell sx={{ ...cellSx, fontWeight: 600, fontFamily: 'monospace' }} align="right">{fmt(totalSpent + laborPaidToDate)}</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600, fontFamily: 'monospace' }} align="right">{fmt(totalTransferCharges)}</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 800, fontFamily: 'monospace', color: brand.primaryDark }} align="right">{fmt(categoryTotal - totalSpent - laborPaidToDate)}</TableCell>
                    <TableCell colSpan={4} sx={{ ...cellSx, borderRight: 'none' }} />
                  </TableRow>

                  {/* Spacer between categories */}
                  <TableRow>
                    <TableCell colSpan={15} sx={{ py: 0.5, bgcolor: '#fff', borderRight: 'none', borderBottom: 'none' }} />
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Category + Save buttons */}
      {!readOnly && (
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => isCreateMode ? addLocalCategory() : (onAddCategory && onAddCategory())}
          sx={{ borderColor: brand.primary, color: brand.primary, borderRadius: 2, textTransform: 'none' }}
        >
          Add Category
        </Button>
        {isCreateMode && onSave && (
          <Button
            variant="contained"
            onClick={() => onSave(localCategories)}
            disabled={saving || localCategories.every(c => c.items.length === 0)}
            sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark }, borderRadius: 2, px: 4, textTransform: 'none' }}
          >
            {saving ? 'Saving...' : 'Save Schedule'}
          </Button>
        )}
      </Box>
      )}
      {laborPaymentCategory && (
        <BOQLaborPaymentDialog
          open={!!laborPaymentCategory}
          onClose={() => setLaborPaymentCategory(null)}
          category={laborPaymentCategory}
          onSubmit={handleSubmitLaborPayment}
          loading={laborPaymentLoading}
        />
      )}
    </Box>
  );
}
